import { EnumMessageRole } from '../enums/message-role.enum';
import { IChatMessage } from '../interfaces/chat-message.interface';
import { IConversationFile } from '../interfaces/conversation-file.interface';
import {
  IMessageAttachment,
  attachmentKey,
  isUserAttachment
} from '../interfaces/message-attachment.interface';
import { formatFileSize } from './attachment.utils';

interface IFileVisual {
  icon: string;
  iconBg: string;
  iconColor: string;
  label: string;
}

// Cores por tipo definidas no handoff de design; o utilitário é TS e não
// alcança os tokens SCSS do módulo.
const PDF: IFileVisual = {
  icon: 'far fa-file-pdf',
  iconBg: '#fdecec',
  iconColor: '#b23b3b',
  label: 'PDF'
};
const SHEET: IFileVisual = {
  icon: 'far fa-file-excel',
  iconBg: '#e8f6ec',
  iconColor: '#2e8b57',
  label: 'Planilha'
};
const TABLE: IFileVisual = {
  icon: 'fas fa-table',
  iconBg: '#e6f0fb',
  iconColor: '#2467c0',
  label: 'CSV'
};
const IMAGE: IFileVisual = {
  icon: 'far fa-image',
  iconBg: '#fff5e0',
  iconColor: '#8a6210',
  label: 'Imagem'
};
const SLIDES: IFileVisual = {
  icon: 'far fa-file-powerpoint',
  iconBg: '#fff0e2',
  iconColor: '#c06213',
  label: 'Apresentação'
};
const GENERIC: IFileVisual = {
  icon: 'far fa-file',
  iconBg: '#eef1f5',
  iconColor: '#566174',
  label: 'Documento'
};

const visualFor = (mime: string, name = ''): IFileVisual => {
  const extensao = name.split('.').pop()?.toLowerCase() ?? '';

  if (mime === 'application/pdf' || extensao === 'pdf') {
    return PDF;
  }
  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extensao)) {
    return IMAGE;
  }
  if (mime === 'text/csv' || extensao === 'csv') {
    return TABLE;
  }
  if (mime.includes('excel') || mime.includes('spreadsheet') || ['xlsx', 'xls'].includes(extensao)) {
    return SHEET;
  }
  if (
    mime.includes('presentation') ||
    mime.includes('powerpoint') ||
    ['pptx', 'ppt'].includes(extensao)
  ) {
    return SLIDES;
  }
  return GENERIC;
};

const formatWhen = (date: Date): string => {
  const hora = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const inicioDeHoje = new Date();
  inicioDeHoje.setHours(0, 0, 0, 0);

  return date.getTime() >= inicioDeHoje.getTime()
    ? `hoje, ${hora}`
    : date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
};

const buildMeta = (
  attachment: IMessageAttachment,
  daAya: boolean,
  quando: Date
): string => {
  const partes = [daAya ? 'Gerado pela AyA' : 'Enviado por você', formatWhen(quando)];
  if (isUserAttachment(attachment) && attachment.sizeBytes) {
    partes.push(formatFileSize(attachment.sizeBytes));
  }
  return partes.join(' · ');
};

const toFile = (
  attachment: IMessageAttachment,
  daAya: boolean,
  quando: Date
): IConversationFile => {
  const visual = visualFor(attachment.mime, attachment.name);

  return {
    fileId: isUserAttachment(attachment) ? attachment.attachmentId : attachment.fileId,
    attachmentId: attachment.attachmentId,
    name: attachment.name,
    meta: buildMeta(attachment, daAya, quando),
    icon: visual.icon,
    iconBg: visual.iconBg,
    iconColor: visual.iconColor,
    attachment
  };
};

/** Reúne os arquivos trocados na conversa, do mais recente para o mais antigo. */
export const collectConversationFiles = (messages: IChatMessage[]): IConversationFile[] => {
  const vistos = new Set<string>();

  return messages
    .reduce<IConversationFile[]>((files, message) => {
      const daAya = message.role === EnumMessageRole.AGENT;

      (message.attachments ?? []).forEach((attachment) => {
        const chave = attachmentKey(attachment);
        if (vistos.has(chave)) {
          return;
        }
        vistos.add(chave);
        files.push(toFile(attachment, daAya, message.timestamp ?? new Date()));
      });

      return files;
    }, [])
    .reverse();
};
